import type { ApiError } from '@/types/api'
import { i18n } from '@/i18n'

/**
 * The wording of a failure comes from the catalogue (i18n/api-errors.ts) so the one part of the app
 * that explains what went wrong speaks the reader's language. The French maps below stay as the
 * fallback for a field the catalogue does not know; vue-i18n returns the key itself when a
 * translation is missing, and a raw `apiErrors.hints.xyz` must never reach a user.
 */
const tr = (key: string, fallback = ''): string => {
  const value = String(i18n.global.t(key))
  return value === key ? fallback : value
}

function catalogueValue(scope: 'fields' | 'hints' | 'codes', key: string): string {
  return tr(`apiErrors.${scope}.${key}`)
}

function solutionLine(hint: string): string {
  return hint ? `${tr('apiErrors.solutionPrefix', 'Solution :')} ${hint}`.trim() : ''
}

type ErrorBag = Record<string, string[] | string>

const FIELD_LABELS: Record<string, string> = {
  agent_id: 'Agent',
  cash_collected_centimes: 'Montant encaissé',
  cod_amount_centimes: 'Montant COD',
  cod_amount_mad: 'Montant COD',
  credit_balance_centimes: 'Crédit partenaire',
  current_password: 'Mot de passe actuel',
  delivery_ids: 'Colis sélectionnés',
  email: 'Email',
  exception_code: 'Motif exception',
  handover_code: 'Code de remise',
  identity_doc: 'Pièce d’identité',
  idempotency_key: 'Requête',
  internal_ref: 'Référence interne',
  operator_tracking_ref: 'Référence opérateur',
  password: 'Mot de passe',
  partner_id: 'Partenaire',
  phone: 'Téléphone',
  photo: 'Photo',
  photos: 'Photos',
  proof_of_residence: 'Justificatif de résidence',
  reason_text: 'Note',
  recipient_address: 'Adresse destinataire',
  recipient_name: 'Nom destinataire',
  recipient_phone: 'Téléphone destinataire',
  residence_id: 'Résidence',
  signature: 'Signature'
}

const FIELD_HINTS: Record<string, string> = {
  agent_id: 'Choisissez un agent actif rattaché à la même résidence, ou laissez vide pour affectation automatique.',
  cod_amount_mad: 'Saisissez un montant en MAD, par exemple 125.50. Laissez 0 si le colis n’est pas COD.',
  credit_balance_centimes: 'Ajoutez du crédit au partenaire avant de créer ou régler ce colis.',
  internal_ref: 'Laissez vide pour générer une référence automatiquement, ou utilisez une valeur unique.',
  operator_tracking_ref: 'Utilisez une référence opérateur unique. Laissez vide sur le formulaire manuel si vous voulez une génération automatique.',
  partner_id: 'Choisissez un partenaire actif avec assez de crédit, ou ajoutez du crédit avant de continuer.',
  recipient_address: 'Ajoutez l’adresse complète du destinataire.',
  recipient_name: 'Ajoutez le nom complet du destinataire.',
  recipient_phone: 'Utilisez le format international, par exemple +212600000000.',
  residence_id: 'Écrivez le nom du point exactement comme dans la liste sous le fichier, ou téléchargez le modèle CSV.',
}

function humanFieldName(field: string): string {
  const normalized = field.replace(/\.\d+/g, '').replace(/\[\d+\]/g, '')

  return catalogueValue('fields', normalized) || FIELD_LABELS[normalized] || normalized
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function normalizeErrorBag(details: unknown): ErrorBag {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return {}
  }

  const errorBag: ErrorBag = {}

  Object.entries(details as Record<string, unknown>).forEach(([key, value]) => {
    if (typeof value === 'string') {
      errorBag[key] = value
      return
    }

    if (Array.isArray(value)) {
      errorBag[key] = value.filter((message): message is string => typeof message === 'string')
    }
  })

  return errorBag
}

function normalizeMessageText(message: unknown): string {
  return String(message ?? '').replace(/\s+/g, ' ').trim()
}

function messageKey(message: unknown): string {
  return normalizeMessageText(message).toLocaleLowerCase()
}

function uniqueTextParts(parts: Array<string | undefined | null>): string[] {
  const seen = new Set<string>()
  const unique: string[] = []

  parts.forEach((part) => {
    const normalized = normalizeMessageText(part)
    const key = messageKey(normalized)

    if (!normalized || seen.has(key)) {
      return
    }

    seen.add(key)
    unique.push(normalized)
  })

  return unique
}

function firstValidationLine(details: ErrorBag, existingMessages: string[] = []): string {
  const existingMessagesKeys = existingMessages.map((message) => messageKey(message)).filter(Boolean)
  const existing = new Set(existingMessagesKeys)
  const seenValidation = new Set<string>()
  const lines: string[] = []

  Object.entries(details).forEach(([field, value]) => {
    const messages = Array.isArray(value) ? value : [value]

    messages.forEach((message) => {
      const normalized = normalizeMessageText(message)
      const key = messageKey(normalized)

      if (!normalized || existing.has(key) || existingMessagesKeys.some((existingKey) => existingKey.includes(key)) || seenValidation.has(key)) {
        return
      }

      seenValidation.add(key)
      lines.push(`${humanFieldName(field)}: ${normalized}`)
    })
  })

  return lines.slice(0, 4).join(' ')
}

function hintsFor(details: ErrorBag): string {
  const hints = Object.keys(details)
    .map((field) => {
      const normalized = field.replace(/\.\d+/g, '').replace(/\[\d+\]/g, '')
      return catalogueValue('hints', normalized) || FIELD_HINTS[normalized]
    })
    .filter((hint, index, all): hint is string => Boolean(hint) && all.indexOf(hint) === index)

  return solutionLine(hints.slice(0, 2).join(' '))
}

export function friendlyApiMessage(error: ApiError | { message?: string; details?: unknown; status?: number } | unknown, fallback = tr('apiErrors.fallback', 'Action impossible. Vérifiez les informations puis réessayez.')): string {
  if (!error || typeof error !== 'object') {
    return fallback
  }

  const apiError = error as {
    message?: string
    code?: string
    field?: string | null
    traceId?: string
    details?: unknown
    status?: number
    name?: string
    response?: {
      data?: {
        message?: string
        errors?: unknown
        error?: {
          code?: string
          message?: string
          field?: string | null
          trace_id?: string
        }
      }
      status?: number
    }
  }

  if (apiError.name === 'ApiRequestError' && apiError.message && !apiError.response) {
    return apiError.message
  }

  const envelopeError = apiError.response?.data?.error
  const message = envelopeError?.message ?? apiError.response?.data?.message ?? apiError.message
  const details = normalizeErrorBag(apiError.response?.data?.errors ?? apiError.details)
  const field = envelopeError?.field ?? apiError.field
  const code = envelopeError?.code ?? apiError.code
  const baseMessage = codeBody(code) || message || fallback
  const validation = firstValidationLine(details, [baseMessage])
  const fieldHint = field ? hintsFor({ [field]: [] }) : ''
  const hint = hintsFor(details)
  const codeHint = hintForCode(code)
  const parts = uniqueTextParts([
    baseMessage,
    validation,
    fieldHint || hint || codeHint
  ])

  if (parts.length === 0 && apiError.status && apiError.status >= 500) {
    return tr('apiErrors.serverError', 'Une erreur serveur est survenue. Réessayez dans un instant ou contactez le support.')
  }

  return parts.join(' ')
}

function hintForCode(code?: string): string {
  return matchCodeHint(code)
}

/**
 * A failure that carries a known code gets its wording from the catalogue instead of the backend's
 * sentence: those bodies are written for developers ("Insufficient wallet balance."), and they used to
 * reach an Arabic-speaking agent verbatim. The backend's own message is kept for codes we do not know,
 * so nothing is ever lost — only translated when there is something better to say.
 */
function codeBody(code?: string): string {
  return code ? tr(`apiErrors.codes.${code}.message`) : ''
}

function matchCodeHint(code?: string): string {
  if (!code) {
    return ''
  }

  return solutionLine(tr(`apiErrors.codes.${code}.hint`))
}

export class ApiRequestError extends Error implements ApiError {
  status?: number
  code?: string
  field?: string | null
  traceId?: string
  details?: unknown

  constructor(error: ApiError) {
    super(friendlyApiMessage(error))
    this.name = 'ApiRequestError'
    this.status = error.status
    this.code = error.code
    this.field = error.field
    this.traceId = error.traceId
    this.details = error.details
  }
}

export function csvRowErrorText(errors: Record<string, string[]>): string {
  const errorBag = normalizeErrorBag(errors)
  const message = firstValidationLine(errorBag)
  const hint = hintsFor(errorBag)

  return uniqueTextParts([
    message,
    hint || solutionLine(tr('apiErrors.hints.csvRow', 'Corrigez cette ligne dans le fichier CSV, puis relancez uniquement les lignes refusées.'))
  ]).join(' ')
}
